import { useEffect, useRef, useState } from 'react';
import { fetchTodayAppointments, rescheduleAppointment } from '@/dashboard/api';
import type { TodayAppointment } from '@/dashboard/types';
import { AcButton, ModalShell } from '@/dashboard/ui';
import { clinicToday } from '@/dashboard/ui/day-scope';
import { formatIstDateTime } from '@/lib/datetime-ist';
import { DaySlotPicker, useDaySlots } from './DaySlotPicker';
import { appointmentErrorMessage } from '@/dashboard/owner/AppointmentWorkbench';

/** A focused host for the existing slot picker and atomic reschedule writer.
 * A lost response is resolved by reading the same appointment, never by
 * automatically submitting another mutation. The host stays mounted while an
 * uncertain result is being checked, so its attempted target is retained. */
export default function LegacyRescheduleAppointmentDialog({ row, clinicId, onClose, onSaved }: {
  row: TodayAppointment;
  clinicId: number;
  onClose: () => void;
  onSaved: (date: string) => void;
}) {
  const [date, setDate] = useState(clinicToday());
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [unknown, setUnknown] = useState(false);
  const [message, setMessage] = useState('');
  const [slotRetry, setSlotRetry] = useState(0);
  const slots = useDaySlots(null, date, clinicId, slotRetry);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const checkSaved = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true);
    try {
      const read = await fetchTodayAppointments(clinicId, 'board', date);
      if (!mounted.current) return;
      const saved = read.appointments.find(item => item.id === row.id);
      if (saved && Date.parse(saved.appointmentTime) === Date.parse(selected)) onSaved(date);
      else {
        // A fresh read has not found the attempted target. Retain the choice;
        // retry uses the original-time fence and server same-target no-op.
        setUnknown(false);
        setMessage('The new time was not found. Review your choice and retry, or keep the appointment.');
      }
    } catch {
      if (mounted.current) setMessage('Could not check the saved appointment. Try checking again.');
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const save = async () => {
    if (inFlight.current || unknown || !selected || slots.kind !== 'ready') return;
    if (!slots.data.slots.some(slot => slot.startIso === selected && !slot.taken && !slot.past)) return;
    inFlight.current = true; setBusy(true); setMessage('');
    try {
      await rescheduleAppointment(row.id, selected, row.appointmentTime);
      if (mounted.current) onSaved(date);
    } catch (error) {
      if (!mounted.current) return;
      const status = error && typeof error === 'object' && 'status' in error ? Number(error.status) : null;
      // A transport/server failure may follow a committed write. Do not
      // label that as a failed save or enable another submission.
      if (status !== null && [400, 401, 403, 404, 409, 422, 503].includes(status)) {
        setMessage(appointmentErrorMessage('reschedule', error));
        setSlotRetry(value => value + 1);
      } else {
        setUnknown(true);
        setMessage('Save result unknown. Check the saved appointment before making another change.');
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const selectable = slots.kind === 'ready' && slots.data.slots.some(slot => slot.startIso === selected && !slot.taken && !slot.past);
  return <ModalShell title="Reschedule appointment" eyebrow="Reception" testId="reschedule-appointment-dialog"
    onClose={onClose} closeDisabled={busy || unknown} closeOnEscape={!busy && !unknown} closeOnScrim={!busy && !unknown} trapFocus
    footer={<>
      <AcButton variant="ghost" disabled={busy || unknown} onClick={onClose}>Keep appointment</AcButton>
      {unknown
        ? <AcButton variant="primary" disabled={busy} onClick={() => void checkSaved()}>{busy ? 'Checking...' : 'Check saved appointment'}</AcButton>
        : <AcButton variant="primary" disabled={busy || !selectable || Date.parse(selected) === Date.parse(row.appointmentTime)} onClick={() => void save()}>{busy ? 'Saving...' : 'Confirm reschedule'}</AcButton>}
    </>}>
    <p>{row.linkedPatientName || row.patientName || 'Patient name unavailable'}</p>
    <p>Appointment #{row.id} · {formatIstDateTime(row.appointmentTime)}</p>
    <p>{row.assignedDoctorName || (row.assignedDoctorId ? 'Doctor name unavailable' : 'Unassigned')}</p>
    <DaySlotPicker presentation="select" date={date} onDateChange={value => { setDate(value); setSelected(''); }} slots={slots}
      selectedIso={selected} onSelectIso={setSelected} disabled={busy || unknown} idPrefix="reception-reschedule" />
    {slots.kind === 'error' && !unknown ? <AcButton variant="ghost" onClick={() => setSlotRetry(value => value + 1)}>Retry available times</AcButton> : null}
    {message ? <p role="alert">{message}</p> : null}
  </ModalShell>;
}
